import { subscription } from "../services/susbscription";

// Page-view credit purchase flow (2026-08-08). Mirrors goalsModel.ts's
// plain fetch-through-the-RPC-service pattern - this isn't a
// high-frequency action needing anything more elaborate.

export interface CreditBlock {
  id: string;
  pageViews: number;
  priceCents: number;
}

export interface PurchaseCreditsResult {
  // null means the saved card charged successfully off-session - nothing
  // further to do. Non-null means the frontend must complete something
  // itself (3D Secure, or no saved card on file at all) via Stripe.js.
  clientSecret: string | null;
  hasSavedPaymentMethod: boolean;
}

export interface CreditPurchaseHistoryItem {
  blockId: string;
  pageViews: number;
  priceCents: number;
  stripePaymentIntentId: string;
  purchasedAt: string;
}

export const getCreditBlocks = async (): Promise<CreditBlock[]> => {
  const response = await subscription({
    id: 0,
    jsonprc: "2.0",
    method: "getCreditBlocks",
    params: {},
  });

  return response?.data?.result || [];
};

export const purchaseCredits = async (
  userId: string,
  blockId: string
): Promise<PurchaseCreditsResult> => {
  const response = await subscription({
    id: 0,
    jsonprc: "2.0",
    method: "purchaseCredits",
    params: { userId, blockId },
  });

  // Found live 2026-08-10: a real Stripe rejection (e.g. "No such
  // PaymentMethod") comes back as a JSON-RPC {error} object with no
  // `result` key at all - blindly returning response?.data?.result gave
  // handleBuy's `result.clientSecret` check `undefined`, crashing with
  // "Cannot read properties of undefined" instead of surfacing the real
  // failure reason. handleBuy's own try/catch was already correct and
  // ready to show error.message in a toast - it just never got a real
  // Error to catch. Unlike getCreditBlocks (a read, where `|| []` is a
  // reasonable empty-state fallback), a purchase failure has no sensible
  // silent default - it must throw.
  if (response?.data?.error) {
    throw new Error(response.data.error.message || "Purchase failed");
  }

  return response?.data?.result;
};

// Powers the Billing tab's credit-purchase history (2026-08-10) - see the
// backend RPC's own comment for why this reads a persisted record instead
// of Stripe's invoice list (a credit PaymentIntent is never attached to an
// Invoice, so it's genuinely invisible to getBillingHistory regardless of
// how that's implemented).
export const getCreditPurchaseHistory = async (
  userId: string
): Promise<CreditPurchaseHistoryItem[]> => {
  const response = await subscription({
    id: 0,
    jsonprc: "2.0",
    method: "getCreditPurchaseHistory",
    params: { userId },
  });

  return response?.data?.result || [];
};
